import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { X, Clock, AlertTriangle, Gift, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TrialBanner() {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (user && token) {
      fetchTrialStatus();
    }
  }, [user, token]);

  const fetchTrialStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrialStatus(data);
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };

  // Don't show if dismissed, not logged in, or not a trial user
  if (dismissed || !user || !trialStatus?.is_trial) return null;

  const { status, days_remaining, is_expired, is_grace_period } = trialStatus;

  // Don't show banner for active trials with more than 7 days
  if (status === 'active' && days_remaining > 7) return null;

  // Determine banner style based on status
  let bgClass = 'bg-blue-600';
  let icon = <Gift className="w-5 h-5" />;
  let message = '';
  let showSubscribeButton = false;

  if (status === 'expiring_soon') {
    if (days_remaining <= 1) {
      bgClass = 'bg-red-600';
      icon = <AlertTriangle className="w-5 h-5" />;
      message = 'Final reminder: Your free trial ends tomorrow. Activate your subscription to continue using BodyCorp365 without interruption.';
    } else if (days_remaining <= 3) {
      bgClass = 'bg-amber-600';
      icon = <AlertTriangle className="w-5 h-5" />;
      message = `Don't lose your progress. Your trial expires in ${days_remaining} days. Your data and setup will be locked if you do not activate your subscription.`;
    } else {
      bgClass = 'bg-blue-600';
      icon = <Clock className="w-5 h-5" />;
      message = `Your trial ends soon. You have ${days_remaining} days left to continue enjoying full access. Upgrade now to avoid interruption.`;
    }
    showSubscribeButton = true;
  } else if (status === 'grace_period') {
    bgClass = 'bg-red-600';
    icon = <AlertTriangle className="w-5 h-5" />;
    message = 'Your trial has ended. Activate your subscription now to retain access and data.';
    showSubscribeButton = true;
  } else if (status === 'subscription_required') {
    bgClass = 'bg-red-700';
    icon = <AlertTriangle className="w-5 h-5" />;
    message = 'Your trial has ended. Activate your subscription to continue using BodyCorp365.';
    showSubscribeButton = true;
  }

  if (!message) return null;

  return (
    <div className={`${bgClass} text-white py-2 px-4 relative`} data-testid="trial-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {icon}
          <span className="text-sm font-medium">{message}</span>
        </div>
        <div className="flex items-center gap-2">
          {showSubscribeButton && (
            <Link to="/subscribe">
              <Button 
                size="sm" 
                variant="secondary"
                className="bg-white text-slate-900 hover:bg-slate-100 font-semibold"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Activate Now
              </Button>
            </Link>
          )}
          {status !== 'subscription_required' && (
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Trial Expired Modal - shown when user tries to do something they can't
export function TrialExpiredModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl animate-in fade-in zoom-in duration-200">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Your Free Trial Has Ended</h2>
          <p className="text-slate-600 mb-4">
            To continue using BodyCorp365, please activate your subscription. Your building data will be preserved for a limited time.
          </p>
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mb-6">
            Don't lose your setup — secure your building today.
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/subscribe" className="w-full">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 font-semibold">
                Activate Subscription
              </Button>
            </Link>
            <Button variant="outline" onClick={onClose} className="w-full">
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to check trial access
export function useTrialAccess() {
  const { user } = useAuth();
  const [access, setAccess] = useState({ access_level: 'full', can_create: true, can_invite: true, can_edit: true });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (user && token) {
      checkAccess();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const checkAccess = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/check-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccess(data);
      }
    } catch (error) {
      console.error('Error checking trial access:', error);
    } finally {
      setLoading(false);
    }
  };

  return { ...access, loading, refresh: checkAccess };
}

export default TrialBanner;
